import { UpdateResponse, UpdateStreamMessage, UpdateStreamMessageNewRecords } from "./sdk.types";
import { libMisc } from "../lib/misc"; // Added for libMisc.wait
import { getAuthInfo } from "../services/sync.service";

const POLLING_INTERVAL_MS = 2000;
const INITIAL_RETRY_DELAY_MS = 5000;
const BACKOFF_RETRY_DELAY_MS = 10000;
const ERROR_THRESHOLD_FOR_OFFLINE = 3;
const MAX_SEND_RETRIES = 5; // New constant
const SEND_RETRY_DELAY_MS = 2000; // New constant

export interface SendMultiMessageItem {
    type: "text" | "media";
    value: string | { blob: Blob; filename: string };
    tmp_id: string;
    direction: "from" | "to";
}

class LibSdk {
    public async *update(
        latestConversationId: string,
        latestMessageId: string,
        latestActionId: string,
        categoryListHash: string,
        firstTime = false
    ): AsyncGenerator<UpdateStreamMessage, void, undefined> {
        const { apiBaseUrl, securityKey } = getAuthInfo();
        let currentLatestConversationId = latestConversationId;
        let currentLatestMessageId = latestMessageId;
        let currentLatestActionId = latestActionId;
        let consecutiveErrors = 0;
        let networkStatus: "online" | "offline" = "online";

        let firstTimeFinalConvId: string | null = null;
        let firstTimeFinalMsgId: string | null = null;
        let firstTimeFinalActionId: string | null = null;
        let firstTimeTotalRecords: number | null = null;
        let firstTimeProgressRecords = 0;
        let initialConvCount: number | null = null;
        let initialMsgCount: number | null = null;
        let initialActionCount: number | null = null;

        while (true) {
            let attemptUrl = `${apiBaseUrl}/update?latest_conv_id=${currentLatestConversationId}&latest_msg_id=${currentLatestMessageId}&latest_action_id=${currentLatestActionId}`;
            if (firstTime && firstTimeFinalConvId !== null && firstTimeFinalMsgId !== null && firstTimeFinalActionId !== null) {
                attemptUrl += `&final_conv_id=${firstTimeFinalConvId}&final_msg_id=${firstTimeFinalMsgId}&final_action_id=${firstTimeFinalActionId}`;
            }

            try {
                const response = await fetch(attemptUrl, {
                    headers: {
                        "Authorization": `Bearer ${securityKey}`
                    }
                });
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status} for URL: ${attemptUrl}`);
                }
                const data: UpdateResponse = await response.json();

                if (networkStatus === "offline") {
                    networkStatus = "online";
                    yield { type: "network_status", status: "online" };
                }
                consecutiveErrors = 0;

                if (data.conv.records.length > 0 || data.msg.records.length > 0 || data.action.records.length > 0 || (data.categories && data.category_list_hash)) {
                    const newRecordsMessage: UpdateStreamMessageNewRecords = {
                        type: "new_records",
                        conv: data.conv.records,
                        msg: data.msg.records,
                        action: data.action.records,
                    };
                    if (data.categories) {
                        newRecordsMessage.categories = data.categories;
                    }
                    if (data.category_list_hash) {
                        newRecordsMessage.category_list_hash = data.category_list_hash;
                    }
                    yield newRecordsMessage;
                }

                let newLatestConvId = currentLatestConversationId;
                if (data.conv.records.length > 0) {
                    newLatestConvId = data.conv.records[data.conv.records.length - 1].id;
                }
                currentLatestConversationId = newLatestConvId;

                let newLatestMsgId = currentLatestMessageId;
                if (data.msg.records.length > 0) {
                    newLatestMsgId = data.msg.records[data.msg.records.length - 1].id;
                }
                currentLatestMessageId = newLatestMsgId;

                let newLatestActionId = currentLatestActionId;
                if (data.action.records.length > 0) {
                    newLatestActionId = data.action.records[data.action.records.length - 1].id;
                }
                currentLatestActionId = newLatestActionId;

                yield {
                    type: "new_latest_ids",
                    latestConversationId: currentLatestConversationId,
                    latestMessageId: currentLatestMessageId,
                    latestActionId: currentLatestActionId,
                };

                if (firstTime) {
                    const numFetchedThisCall = data.conv.records.length + data.msg.records.length + data.action.records.length;
                    firstTimeProgressRecords += numFetchedThisCall;

                    if (firstTimeTotalRecords === null) { // First successful call in firstTime mode
                        firstTimeFinalConvId = data.conv.finalId;
                        firstTimeFinalMsgId = data.msg.finalId;
                        firstTimeFinalActionId = data.action.finalId;
                        initialConvCount = data.conv.count;
                        initialMsgCount = data.msg.count;
                        initialActionCount = data.action.count;
                        firstTimeTotalRecords = initialConvCount + initialMsgCount + initialActionCount;
                    }

                    if (firstTimeTotalRecords !== null) {
                        const isFirstSuccessfulFetchAndGotEverything =
                            (firstTimeTotalRecords === firstTimeProgressRecords) &&
                            (firstTimeTotalRecords === numFetchedThisCall);

                        if (firstTimeTotalRecords > 0 && !isFirstSuccessfulFetchAndGotEverything) {
                            yield {
                                type: "first_load_progress",
                                progress: firstTimeProgressRecords,
                                total: firstTimeTotalRecords,
                            };
                        }

                        const convDone = (initialConvCount === 0) || (firstTimeFinalConvId !== null && currentLatestConversationId >= firstTimeFinalConvId);
                        const msgDone = (initialMsgCount === 0) || (firstTimeFinalMsgId !== null && currentLatestMessageId >= firstTimeFinalMsgId);
                        const actionDone = (initialActionCount === 0) || (firstTimeFinalActionId !== null && currentLatestActionId >= firstTimeFinalActionId);

                        if (convDone && msgDone && actionDone) {
                            return;
                        }
                    }
                } else {
                    await new Promise(resolve => setTimeout(resolve, POLLING_INTERVAL_MS));
                }

            } catch (error) {
                console.error("Update SDK error:", error);
                consecutiveErrors++;
                if (consecutiveErrors >= ERROR_THRESHOLD_FOR_OFFLINE && networkStatus === "online") {
                    networkStatus = "offline";
                    yield { type: "network_status", status: "offline" };
                }

                const delay = consecutiveErrors >= ERROR_THRESHOLD_FOR_OFFLINE ? BACKOFF_RETRY_DELAY_MS : INITIAL_RETRY_DELAY_MS;
                await new Promise(resolve => setTimeout(resolve, delay));
            }
        }
    }

    public async send(
        convId: string,
        text: string,
        tmpId: string,
        media?: { blob: Blob; ext: string }
    ): Promise<boolean> {
        const { apiBaseUrl, securityKey } = getAuthInfo();
        // await libMisc.wait(2000);

        const baseUrl = `${apiBaseUrl}/send`;

        for (let attempt = 0; attempt < MAX_SEND_RETRIES; attempt++) {
            try {
                let fetchUrl = baseUrl;
                let fetchOptions: RequestInit;

                if (media) {
                    // --- MEDIA CASE ---
                    // Validate extension and pick content type
                    const ext = media.ext.toLowerCase();
                    let contentType: string;
                    if (ext === "png") {
                        contentType = "image/png";
                    } else if (ext === "jpg" || ext === "jpeg") {
                        contentType = "image/jpeg";
                    } else {
                        throw new Error(`Invalid media extension: ${media.ext}`);
                    }

                    // Build query string with forced text="attachment" and media=true
                    const params = new URLSearchParams({
                        conv_id: convId,
                        text: "attachment",
                        tmp_id: tmpId,
                        media: "true",
                    });
                    fetchUrl = `${baseUrl}?${params.toString()}`;

                    fetchOptions = {
                        method: "POST",
                        headers: {
                            "Content-Type": contentType,
                            "Authorization": `Bearer ${securityKey}`
                        },
                        body: media.blob,
                    };
                } else {
                    // --- TEXT CASE (unchanged) ---
                    const payload = {
                        conv_id: convId,
                        text,
                        tmp_id: tmpId,
                    };
                    fetchOptions = {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": `Bearer ${securityKey}`
                        },
                        body: JSON.stringify(payload),
                    };
                }

                const response = await fetch(fetchUrl, fetchOptions);

                if (response.ok) {
                    try {
                        const responseData = await response.json();
                        if (responseData.result === "success") {
                            return true;
                        } else if (responseData.result === "failed") {
                            return false;
                        } else {
                            console.error(
                                "Send SDK error: Received OK status but unexpected response content.",
                                responseData
                            );
                            return false;
                        }
                    } catch (jsonError) {
                        console.error(
                            "Send SDK error: Failed to parse JSON from OK response.",
                            jsonError
                        );
                        return false;
                    }
                } else {
                    let errorBody = "";
                    try {
                        errorBody = await response.text();
                    } catch {
                        errorBody = "(Could not read error response body)";
                    }
                    console.error(
                        `Send SDK error: HTTP error! status: ${response.status}, body: ${errorBody}`
                    );
                    return false;
                }
            } catch (error) {
                console.error(
                    `Send SDK connection error (attempt ${attempt + 1}/${MAX_SEND_RETRIES}):`,
                    error
                );
                if (attempt < MAX_SEND_RETRIES - 1) {
                    await libMisc.wait(SEND_RETRY_DELAY_MS);
                } else {
                    console.error("Send SDK error: All retry attempts failed.");
                }
            }
        }
        return false;
    }

    public async upload(blob: Blob, filename: string): Promise<boolean> {
        const { apiBaseUrl, securityKey } = getAuthInfo();
        const filenameRegex = /^[a-f0-9]{16}\.(png|jpg|jpeg|gif|webp)$/;
        if (!filenameRegex.test(filename)) {
            console.error(`Upload SDK error: Invalid filename format: ${filename}`);
            return false;
        }

        const url = `${apiBaseUrl}/upload?filename=${encodeURIComponent(filename)}`;

        for (let attempt = 0; attempt < MAX_SEND_RETRIES; attempt++) {
            try {
                const response = await fetch(url, {
                    method: "PUT",
                    headers: {
                        "Authorization": `Bearer ${securityKey}`,
                        // Content-Type will be set by the browser for Blob
                    },
                    body: blob,
                });

                if (response.ok) {
                    const responseData = await response.json();
                    if (responseData.result === "success") {
                        return true;
                    }
                    console.error("Upload SDK error: Received OK status but unexpected response content.", responseData);
                    return false; // Or retry based on specific non-success result if applicable
                }
                let errorBody = "";
                try { errorBody = await response.text(); } catch { errorBody = "(Could not read error response body)"; }
                console.error(`Upload SDK error: HTTP error! status: ${response.status}, body: ${errorBody}`);
                // Consider not retrying for certain HTTP errors (e.g., 400, 401, 403)
                // For now, retrying on all non-OK responses
            } catch (error) {
                console.error(`Upload SDK connection error (attempt ${attempt + 1}/${MAX_SEND_RETRIES}):`, error);
            }
            if (attempt < MAX_SEND_RETRIES - 1) {
                await libMisc.wait(SEND_RETRY_DELAY_MS);
            }
        }
        console.error("Upload SDK error: All retry attempts failed.");
        return false;
    }

    public async sendMulti(convId: string, messages: SendMultiMessageItem[]): Promise<boolean> {
        const { apiBaseUrl, securityKey } = getAuthInfo();

        // Validate messages
        const tmpIdRegex = /^[a-zA-Z0-9]{5}$/;
        const filenameRegex = /^[a-f0-9]{16}\.(png|jpg|jpeg|gif|webp)$/;
        const seenTmpIds = new Set<string>();

        for (const msg of messages) {
            if (!tmpIdRegex.test(msg.tmp_id)) {
                console.error(`sendMulti SDK error: Invalid tmp_id format: ${msg.tmp_id}`);
                return false;
            }
            if (seenTmpIds.has(msg.tmp_id)) {
                console.error(`sendMulti SDK error: Duplicate tmp_id: ${msg.tmp_id}`);
                return false;
            }
            seenTmpIds.add(msg.tmp_id);

            if (msg.type === "text") {
                if (typeof msg.value !== 'string' || msg.value.trim() === "") {
                    console.error(`sendMulti SDK error: Invalid value for text message (tmp_id: ${msg.tmp_id}). Must be non-empty string.`);
                    return false;
                }
            } else if (msg.type === "media") {
                if (typeof msg.value !== 'object' || !msg.value.blob || !msg.value.filename) {
                    console.error(`sendMulti SDK error: Invalid value for media message (tmp_id: ${msg.tmp_id}). Must be {blob, filename}.`);
                    return false;
                }
                if (!(msg.value.blob instanceof Blob)) {
                     console.error(`sendMulti SDK error: Media value's blob is not a Blob instance (tmp_id: ${msg.tmp_id}).`);
                     return false;
                }
                if (!filenameRegex.test(msg.value.filename)) {
                    console.error(`sendMulti SDK error: Invalid filename format for media message (tmp_id: ${msg.tmp_id}): ${msg.value.filename}`);
                    return false;
                }
            } else {
                console.error(`sendMulti SDK error: Invalid message type: ${(msg as any).type}`);
                return false;
            }
            if (msg.direction !== "from" && msg.direction !== "to") {
                console.error(`sendMulti SDK error: Invalid direction: ${msg.direction} for tmp_id: ${msg.tmp_id}. Must be "from" or "to".`);
                return false;
            }
        }

        // Upload media files first
        for (const msg of messages) {
            if (msg.type === "media") {
                const mediaValue = msg.value as { blob: Blob; filename: string };
                const uploadSuccess = await this.upload(mediaValue.blob, mediaValue.filename);
                if (!uploadSuccess) {
                    console.error(`sendMulti SDK error: Failed to upload media (filename: ${mediaValue.filename}) for tmp_id: ${msg.tmp_id}`);
                    return false; // Stop if any upload fails
                }
            }
        }

        // Prepare payload for /send endpoint
        const payloadMessages = messages.map(msg => {
            return {
                type: msg.type,
                value: msg.type === "media" ? (msg.value as { blob: Blob; filename: string }).filename : msg.value as string,
                tmp_id: msg.tmp_id,
                direction: msg.direction,
            };
        });

        const sendPayload = {
            conv_id: convId,
            messages: payloadMessages,
        };

        const url = `${apiBaseUrl}/send`;
        for (let attempt = 0; attempt < MAX_SEND_RETRIES; attempt++) {
            try {
                const response = await fetch(url, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${securityKey}`,
                    },
                    body: JSON.stringify(sendPayload),
                });

                if (response.ok) {
                    const responseData = await response.json();
                    if (responseData.result === "success") {
                        return true;
                    }
                    console.error("sendMulti SDK error (/send): Received OK status but unexpected response content.", responseData);
                    return false;
                }
                let errorBody = "";
                try { errorBody = await response.text(); } catch { errorBody = "(Could not read error response body)"; }
                console.error(`sendMulti SDK error (/send): HTTP error! status: ${response.status}, body: ${errorBody}`);

            } catch (error) {
                console.error(`sendMulti SDK connection error (/send) (attempt ${attempt + 1}/${MAX_SEND_RETRIES}):`, error);
            }
            if (attempt < MAX_SEND_RETRIES - 1) {
                await libMisc.wait(SEND_RETRY_DELAY_MS);
            }
        }
        console.error("sendMulti SDK error (/send): All retry attempts failed.");
        return false;
    }

    public async setConversationCategory(
        convId: string,
        categoryId: string
    ): Promise<boolean> {
        const { apiBaseUrl, securityKey } = getAuthInfo();
        const url = `${apiBaseUrl}/set_conversation_category`;

        for (let attempt = 0; attempt < MAX_SEND_RETRIES; attempt++) {
            try {
                const payload = {
                    conv_id: convId,
                    category_id: categoryId,
                };
                const fetchOptions: RequestInit = {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${securityKey}`
                    },
                    body: JSON.stringify(payload),
                };

                const response = await fetch(url, fetchOptions);

                if (response.ok) {
                    try {
                        const responseData = await response.json();
                        if (responseData.result === "success") {
                            return true;
                        } else if (responseData.result === "failed") {
                            return false;
                        } else {
                            console.error(
                                "SetConversationCategory SDK error: Received OK status but unexpected response content.",
                                responseData
                            );
                            return false;
                        }
                    } catch (jsonError) {
                        console.error(
                            "SetConversationCategory SDK error: Failed to parse JSON from OK response.",
                            jsonError
                        );
                        return false;
                    }
                } else {
                    let errorBody = "";
                    try {
                        errorBody = await response.text();
                    } catch {
                        errorBody = "(Could not read error response body)";
                    }
                    console.error(
                        `SetConversationCategory SDK error: HTTP error! status: ${response.status}, body: ${errorBody}`
                    );
                    return false;
                }
            } catch (error) {
                console.error(
                    `SetConversationCategory SDK connection error (attempt ${attempt + 1}/${MAX_SEND_RETRIES}):`,
                    error
                );
                if (attempt < MAX_SEND_RETRIES - 1) {
                    await libMisc.wait(SEND_RETRY_DELAY_MS);
                } else {
                    console.error("SetConversationCategory SDK error: All retry attempts failed.");
                }
            }
        }
        return false;
    }

    public async toggleUserCategorySubscription(
        categoryId: string,
        state: "ON" | "OFF"
    ): Promise<boolean> {
        const { apiBaseUrl, securityKey } = getAuthInfo();
        const url = `${apiBaseUrl}/toggle_user_category_subscription`;

        for (let attempt = 0; attempt < MAX_SEND_RETRIES; attempt++) {
            try {
                const payload = {
                    category_id: categoryId,
                    state: state,
                };
                const fetchOptions: RequestInit = {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${securityKey}`
                    },
                    body: JSON.stringify(payload),
                };

                const response = await fetch(url, fetchOptions);

                if (response.ok) {
                    try {
                        const responseData = await response.json();
                        if (responseData.result === "success") {
                            return true;
                        } else if (responseData.result === "failed") {
                            return false;
                        } else {
                            console.error(
                                "ToggleUserCategorySubscription SDK error: Received OK status but unexpected response content.",
                                responseData
                            );
                            return false;
                        }
                    } catch (jsonError) {
                        console.error(
                            "ToggleUserCategorySubscription SDK error: Failed to parse JSON from OK response.",
                            jsonError
                        );
                        return false;
                    }
                } else {
                    let errorBody = "";
                    try {
                        errorBody = await response.text();
                    } catch {
                        errorBody = "(Could not read error response body)";
                    }
                    console.error(
                        `ToggleUserCategorySubscription SDK error: HTTP error! status: ${response.status}, body: ${errorBody}`
                    );
                    return false;
                }
            } catch (error) {
                console.error(
                    `ToggleUserCategorySubscription SDK connection error (attempt ${attempt + 1}/${MAX_SEND_RETRIES}):`,
                    error
                );
                if (attempt < MAX_SEND_RETRIES - 1) {
                    await libMisc.wait(SEND_RETRY_DELAY_MS);
                } else {
                    console.error("ToggleUserCategorySubscription SDK error: All retry attempts failed.");
                }
            }
        }
        return false;
    }
}

export const libSdk = new LibSdk();